import React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Skeleton,
  Typography,
} from "@material-ui/core";
import PropTypes from "prop-types";
import ModuleSwitchGrid from "src/modules/bundles/components/ModuleSwitchGrid";
import {
  SELECTION_EXISTING,
  SELECTION_NEW,
  describeSelection,
  matchedBundleBannerMessage,
  moduleIdsForBundle,
  toggleModuleId,
} from "src/modules/bundles/utils/bundle-modules";
import {
  CATALOGUE_UNAVAILABLE_MESSAGE,
  NO_BUNDLE,
  NO_BUNDLES_HINT,
  describeCatalogue,
} from "src/modules/bundles/utils/catalogue-state";
import TextField from "./white-text-field.component";
import { isApprove } from "../approval-wizard.constants";

const AccessRightsStep = ({
  values,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  setValues,
  bundleOptions,
  moduleListing,
  isLoaded,
  hasFailed,
  onRetry,
  isFetching,
  isSubmitting,
  canEdit,
  classes,
}) => {
  const moduleIds = values.moduleIds || [];
  // One computation drives the whole step: what to render, whether a name is required
  // (validation schema in the shell), and what the container ends up assigning.
  const selection = describeSelection(bundleOptions, values.bundle, moduleIds);

  // The SAME gate the two company surfaces render behind. This step used to ask the
  // length-based question instead — `!moduleListing.length` reads a catalogue that came back
  // empty as one that has not arrived, so the step showed a skeleton for ever; and with no
  // live bundles the dropdown offered nothing but "None" while the schema demands a bundle to
  // approve, leaving the admin with an unapprovable company and no explanation.
  const {
    isReady,
    hasFailedToLoad,
    hasNoBundles,
    hasNoModules,
  } = describeCatalogue({
    isLoaded,
    isLoading: isFetching,
    hasFailed,
    bundleOptions,
    moduleListing,
  });

  // Picking a bundle re-seeds the switches from that bundle and drops any half-typed new
  // bundle name — the admin is starting from a different place.
  //
  // ONE setValues, never three setFieldValue calls — the same rule the shared editor states
  // at length. Formik 2.4.5's setFieldValue validates `setIn(state.values, field, value)`
  // against the values of the render it was created in; dispatch replaces that object rather
  // than mutating it, and inside a batched event handler no commit intervenes to hand the
  // handler a fresher one. So three calls validate three half-applied selections and none of
  // them the real one. On "Use it" all three still read SELECTION_EXISTING, so the schema's
  // bundle test kept the error alive under a dropdown that by then showed the matched bundle,
  // with the banner — and the button that would have fixed it — gone.
  const selectBundle = (bundleId) => {
    setValues((previous) => ({
      ...previous,
      bundle: bundleId,
      moduleIds: moduleIdsForBundle(bundleOptions, bundleId),
      newBundleName: "",
    }));
  };

  return (
    <>
      {/* Always rendered — a viewer (no canEdit) sees the same step, read-only: the bundle
          picker and every module switch are disabled, and the mutation-only affordances
          (the "Use it" shortcut and the new-bundle name field) are hidden. The footer's
          approve/reject buttons are already gated by canEdit in the shell. */}
      {isApprove(values.approval) && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bundle"
                name="bundle"
                onChange={(event) => {
                  selectBundle(event.target.value);
                }}
                onBlur={handleBlur}
                helperText={touched.bundle && errors.bundle}
                error={Boolean(touched.bundle && errors.bundle)}
                required
                select
                value={values.bundle}
                variant="outlined"
                disabled={isSubmitting || !canEdit}
              >
                <MenuItem value={NO_BUNDLE}>
                  <em>None</em>
                </MenuItem>
                {(bundleOptions || []).map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Box py={3}>
            <Divider />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box flexGrow={1}>
              <Typography variant="body2" className={classes.labelText}>
                <b>Assign access right</b>
              </Typography>
            </Box>
            {/* The whole catalogue is on screen, so the honest number is how much of it
                is switched on — not "what the bundle carries". Withheld until both lists
                have landed: "0 of 0" while they are in flight is a lie. */}
            {isReady && (
              <Typography variant="caption" color="textSecondary">
                {moduleIds.length} of {(moduleListing || []).length} module(s) on
              </Typography>
            )}
          </Box>
          <Box mt={2}>
            {/* Five states, none of which may render an empty box or an endless skeleton,
                in this order: the catalogue read failed outright, catalogues not back yet,
                no bundle picked, catalogues back but the module one is empty, ready. */}
            {hasFailedToLoad ? (
              /* Nothing ever arrived and the read that would have delivered it failed,
                 so waiting will not help. Say so and offer the way out, rather than a
                 skeleton that stays put.
                 This is tested FIRST on purpose: the wizard opens on NO_BUNDLE, and a
                 failed catalogue read leaves the dropdown empty — so the branch below
                 would otherwise win every time and tell the admin to "pick one above"
                 when there is nothing above to pick, with the Retry unreachable. */
              <Alert
                severity="warning"
                action={(
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                )}
              >
                {CATALOGUE_UNAVAILABLE_MESSAGE}
              </Alert>
            ) : !isReady ? (
              <>
                <Skeleton variant="text" animation="wave" height={25} />
                <Skeleton variant="text" animation="wave" height={25} />
                <Skeleton variant="text" animation="wave" height={25} />
                <Skeleton variant="text" animation="wave" height={25} />
              </>
            ) : values.bundle === NO_BUNDLE ? (
              <Typography variant="caption" color="textSecondary">
                No bundle selected — pick one above to see and adjust its modules. An
                approval has to assign a bundle.
                {hasNoBundles && NO_BUNDLES_HINT}
              </Typography>
            ) : hasNoModules ? (
              <Typography variant="caption" color="textSecondary">
                No modules are available to show. The selected bundle is assigned as it
                stands, with the access rights it already grants.
              </Typography>
            ) : (
              <>
                {/* Not an FYI about something the approval is about to do on its own —
                    this state BLOCKS the approve (the schema's bundle test refuses it),
                    so it is a warning, and "Use it" is the one-click way forward: it
                    puts the matched bundle in the dropdown, which makes the selection
                    unchanged and clears the error. On screen from the moment the
                    switches drift, so the block is never a surprise at the button.
                    Behind `canEdit` because only an editor can act on it: a viewer's
                    switches are disabled, so the selection cannot drift under them, and
                    the footer that the block would refuse is not rendered for them. */}
                {canEdit && selection.status === SELECTION_EXISTING && (
                  <Box mb={2}>
                    <Alert
                      severity="warning"
                      action={(
                        <Button
                          color="inherit"
                          size="small"
                          disabled={isSubmitting}
                          onClick={() => selectBundle(selection.matched.id)}
                        >
                          Use it
                        </Button>
                      )}
                    >
                      {matchedBundleBannerMessage(selection.matched)}
                    </Alert>
                  </Box>
                )}
                {canEdit && selection.status === SELECTION_NEW && (
                  <Box mb={2}>
                    <TextField
                      fullWidth
                      label="New bundle name"
                      name="newBundleName"
                      placeholder="e.g. Enterprise + PRIFE"
                      required
                      value={values.newBundleName}
                      onChange={(event) => {
                        setFieldValue("newBundleName", event.target.value);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(touched.newBundleName && errors.newBundleName)}
                      helperText={
                        (touched.newBundleName && errors.newBundleName)
                        || "No bundle grants this combination. On approve it is saved as a new bundle and that bundle is assigned to this company."
                      }
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Box>
                )}
                <ModuleSwitchGrid
                  moduleListing={moduleListing}
                  moduleIds={moduleIds}
                  onToggle={(id) => setFieldValue("moduleIds", toggleModuleId(moduleIds, id))}
                  disabled={isSubmitting || !canEdit}
                />
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
};

AccessRightsStep.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  // Whole-form update, used wherever more than one field changes at once. See selectBundle.
  setValues: PropTypes.func.isRequired,
  bundleOptions: PropTypes.array,
  moduleListing: PropTypes.array,
  // Both catalogues have come back at least once. Not "are non-empty".
  isLoaded: PropTypes.bool,
  // A catalogue read was rejected. Only acted on while nothing has ever loaded.
  hasFailed: PropTypes.bool,
  onRetry: PropTypes.func,
  isFetching: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  canEdit: PropTypes.bool,
  classes: PropTypes.object.isRequired,
};

export default AccessRightsStep;
